import { type BlockquoteOptions } from '@tiptap/extension-blockquote'
import { type BoldOptions } from '@tiptap/extension-bold'
import {
  BubbleMenu,
  type BubbleMenuOptions,
} from '@tiptap/extension-bubble-menu'
import { type CodeOptions } from '@tiptap/extension-code'
import { type CodeBlockOptions } from '@tiptap/extension-code-block'
import {
  Collaboration,
  type CollaborationOptions,
} from '@tiptap/extension-collaboration'
import { type DropcursorOptions } from '@tiptap/extension-dropcursor'
import { type HardBreakOptions } from '@tiptap/extension-hard-break'
import { type HeadingOptions } from '@tiptap/extension-heading'
import { Highlight, type HighlightOptions } from '@tiptap/extension-highlight'
import { type HorizontalRuleOptions } from '@tiptap/extension-horizontal-rule'
import { type ImageOptions } from '@tiptap/extension-image'
import { type ItalicOptions } from '@tiptap/extension-italic'
import { type LinkOptions } from '@tiptap/extension-link'
import {
  type BulletListOptions,
  TaskItem,
  type TaskItemOptions,
  TaskList,
  type TaskListOptions,
  type OrderedListOptions,
  type ListItemOptions,
  type ListKeymapOptions,
} from '@tiptap/extension-list'
import { Mention, type MentionOptions } from '@tiptap/extension-mention'
import { type ParagraphOptions } from '@tiptap/extension-paragraph'
import { type StrikeOptions } from '@tiptap/extension-strike'
import {
  Subscript,
  type SubscriptExtensionOptions,
} from '@tiptap/extension-subscript'
import {
  Superscript,
  type SuperscriptExtensionOptions,
} from '@tiptap/extension-superscript'
import {
  type TableRowOptions,
  type TableCellOptions,
  type TableHeaderOptions,
  TableCell,
  TableHeader,
  TableRow,
} from '@tiptap/extension-table'
import { TextAlign, type TextAlignOptions } from '@tiptap/extension-text-align'
import {
  Color,
  type ColorOptions,
  TextStyle,
  type TextStyleOptions,
} from '@tiptap/extension-text-style'
import {
  Typography,
  type TypographyOptions,
} from '@tiptap/extension-typography'
import { type UnderlineOptions } from '@tiptap/extension-underline'
import {
  Focus,
  type FocusOptions,
  type UndoRedoOptions,
  Placeholder,
  type PlaceholderOptions,
  type TrailingNodeOptions,
  type SelectionOptions,
} from '@tiptap/extensions'
import { AiToolkit, type AiToolkitOptions } from '@tiptap-pro/ai-toolkit'
import { CommentsKit, type ThreadOptions } from '@tiptap-pro/extension-comments'
import {
  Snapshot,
  type SnapshotOptions,
  type SnapshotStorage,
} from '@tiptap-pro/extension-snapshot'
import {
  SnapshotCompare,
  type SnapshotCompareOptions,
} from '@tiptap-pro/extension-snapshot-compare'
import type {
  ExtensionsConfig,
  StarterKitExtensionsConfig,
} from '../hooks/useExtensions/types'
import { Asset, type AssetOptions } from './Asset'
import { CenterJustify, type CenterJustifyOptions } from './CenterJustify'
import { CharacterCount, type CharacterCountOptions } from './CharacterCount'
import {
  CollaborationCaret,
  type CollaborationCaretOptions,
} from './CollaborationCaret'
import {
  EmbeddedResource,
  type EmbeddedResourceOptions,
} from './EmbeddedResource'
import { ExtendedImage, type ExtendedImageOptions } from './ExtendedImage'
import { Gapcursor } from './Gapcursor'
import { ListItemBackspace } from './ListItemBackspace'
import { Normalize } from './Normalize'
import { ResourceFile } from './ResourceFile'
import { Selection } from './Selection'
import { SingleLineDocument } from './SingleLineDocument'
import {
  SocialLinkDecorator,
  type SocialLinkDecoratorOptions,
} from './SocialLinkDecorator'
import { Table, type TableOptions } from './Table'

/**
 * Workaround for Tiptap Pro extensions that don't declare
 * their storage types on the Storage interface.
 *
 * Can be removed once these extensions add Storage declarations.
 */
declare module '@tiptap/core' {
  interface Storage {
    snapshot: SnapshotStorage
  }
}

export interface ExtensionOptions {
  aiToolkit: AiToolkitOptions
  /* Begin starter kit options */
  blockquote: BlockquoteOptions
  bold: BoldOptions
  bulletList: BulletListOptions
  code: CodeOptions
  codeBlock: CodeBlockOptions
  document: undefined
  dropcursor: DropcursorOptions
  gapcursor: undefined
  hardBreak: HardBreakOptions
  heading: HeadingOptions
  horizontalRule: HorizontalRuleOptions
  italic: ItalicOptions
  link: LinkOptions
  listItem: ListItemOptions
  listKeymap: ListKeymapOptions
  orderedList: OrderedListOptions
  paragraph: ParagraphOptions
  strike: StrikeOptions
  text: undefined
  trailingNode: TrailingNodeOptions
  underline: UnderlineOptions
  undoRedo: UndoRedoOptions
  /* End starter kit options */
  asset: AssetOptions
  bubbleMenu: BubbleMenuOptions
  centerJustify: CenterJustifyOptions
  characterCount: CharacterCountOptions
  checkbox: TaskItemOptions
  collaboration: CollaborationOptions
  collaborationCaret: CollaborationCaretOptions
  color: ColorOptions
  comments: ThreadOptions
  embeddedResource: EmbeddedResourceOptions
  extendedImage: ExtendedImageOptions
  focus: FocusOptions
  highlight: HighlightOptions
  image: ImageOptions
  mention: MentionOptions
  normalize: undefined
  placeholder: PlaceholderOptions
  resourceFile: AssetOptions
  selection: SelectionOptions
  singleLineDocument: undefined
  snapshot: SnapshotOptions
  snapshotCompare: SnapshotCompareOptions
  socialLinkDecorator: SocialLinkDecoratorOptions
  subscript: SubscriptExtensionOptions
  superscript: SuperscriptExtensionOptions
  table: TableOptions
  tableHeader: TableHeaderOptions
  tableRow: TableRowOptions
  tableCell: TableCellOptions
  listItemBackspace: undefined
  taskList: TaskListOptions
  textAlign: TextAlignOptions
  textStyle: TextStyleOptions
  typography: TypographyOptions
}

export const defaultStarterKitDefinition: StarterKitExtensionsConfig = {
  blockquote: { config: true },
  bold: { config: true },
  bulletList: { config: true },
  code: { config: false },
  codeBlock: { config: false },
  document: { config: true },
  dropcursor: { config: { class: 'dropcursor', width: 2 } },
  gapcursor: { config: true, extension: Gapcursor },
  hardBreak: { config: true },
  heading: { config: { levels: [1, 2, 3, 4] } },
  horizontalRule: { config: true },
  italic: { config: true },
  link: {
    config: {
      autolink: true,
      openOnClick: false,
      protocols: ['tel', 'mailto'],
    },
  },
  listKeymap: { config: true },
  listItem: { config: true },
  orderedList: { config: true },
  paragraph: { config: true },
  strike: { config: true },
  text: { config: true },
  trailingNode: { config: { notAfter: ['centerJustify'] } },
  underline: { config: true },
  undoRedo: { config: true },
}

export const defaultDefinition: ExtensionsConfig = {
  ...defaultStarterKitDefinition,
  aiToolkit: { extension: AiToolkit, config: false },
  asset: { extension: Asset },
  bubbleMenu: { extension: BubbleMenu, config: false },
  centerJustify: { extension: CenterJustify, config: false },
  characterCount: { extension: CharacterCount, config: false },
  checkbox: {
    extension: TaskItem,
    config: { nested: true },
  },
  collaboration: { extension: Collaboration, config: false },
  collaborationCaret: { extension: CollaborationCaret, config: false },
  color: { extension: Color },
  comments: { extension: CommentsKit, config: false },
  embeddedResource: { extension: EmbeddedResource },
  extendedImage: { extension: ExtendedImage },
  focus: { extension: Focus },
  highlight: { extension: Highlight, config: { multicolor: true } },
  mention: { extension: Mention, config: false },
  normalize: { extension: Normalize },
  placeholder: {
    extension: Placeholder,
    config: { placeholder: '', showOnlyWhenEditable: true },
  },
  resourceFile: { extension: ResourceFile },
  selection: { extension: Selection },
  singleLineDocument: { extension: SingleLineDocument, config: false },
  snapshot: { extension: Snapshot, config: false },
  snapshotCompare: { extension: SnapshotCompare, config: false },
  socialLinkDecorator: { extension: SocialLinkDecorator, config: false },
  subscript: { extension: Subscript, config: true },
  superscript: { extension: Superscript, config: true },
  listItemBackspace: { extension: ListItemBackspace },
  taskList: {
    extension: TaskList,
    config: { HTMLAttributes: { class: 'task-list' } },
  },
  table: {
    extension: Table,
    config: {
      cellMinWidth: 96,
      resizable: true,
      // Tables with content fill the editor width, so dragging the
      // last column edge makes the table overflow and scroll.
      lastColumnResizable: false,
    },
  },
  tableCell: { extension: TableCell },
  tableHeader: { extension: TableHeader },
  tableRow: { extension: TableRow },
  textAlign: {
    extension: TextAlign,
    config: {
      types: ['heading', 'paragraph', 'bulletList', 'orderedList', 'taskList'],
      alignments: ['left', 'center', 'right', 'justify'],
    },
  },
  textStyle: { extension: TextStyle },
  typography: { extension: Typography },
}
